import { MessageSquare, Plus, Search } from "lucide-react";
import type { CSSProperties } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { clerkEnabled } from "@/services/clerk";
import { useMe } from "@/features/members/hooks/useMe";
import { MembersOnly } from "@/components/MembersOnly";
import {
  useCreateThread,
  useThreads,
} from "@/features/discussions/discussions";
import { RowListSkeleton } from "@/components/Skeletons";
import { timeAgo } from "@/lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

const ALL = "__all__";

export default function DiscussionsPage() {
  const navigate = useNavigate();
  const { data: me } = useMe(clerkEnabled);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState(ALL);
  const { data, error } = useThreads(search, tag === ALL ? null : tag);
  const createThread = useCreateThread();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tags: "" });
  const isStaff = Boolean(me?.member && me.member.role !== "member");

  function handleCreate() {
    createThread.mutate(
      {
        title: form.title,
        body: form.body,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      },
      {
        onSuccess: (thread) => {
          setDialogOpen(false);
          setForm({ title: "", body: "", tags: "" });
          if (isStaff) {
            navigate(`/discussions/${thread.id}`);
          } else {
            toast.success(
              "Posted. Your thread will appear once a moderator approves it"
            );
          }
        },
        onError: (err) => toast.error(err.message),
      }
    );
  }

  // The one surface with no preview to offer: every thread is a member
  // writing to other members, so there is no redacted version of it that is
  // still worth reading. Say so, rather than an empty page.
  if (error) {
    return (
      <div className="max-w-4xl">
        <h1 className="text-2xl">Discussions</h1>
        <MembersOnly what="Discussion between members" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl">Discussions</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Members-only conversations. Posts are reviewed before they go live.
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus data-slot="icon" /> New discussion
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start a discussion</DialogTitle>
              <DialogDescription>
                {isStaff
                  ? "Your post publishes immediately."
                  : "Your post will appear after a moderator approves it."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  placeholder="What do you want to ask or share?"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  rows={6}
                  placeholder="Links you include will be surfaced for the resource library."
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  placeholder="licensing, digitization…"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={handleCreate}
                disabled={!form.title || !form.body || createThread.isPending}
              >
                {createThread.isPending && <Spinner data-slot="icon" />}
                {isStaff ? "Publish" : "Submit for review"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <div className="relative min-w-64 flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            className="pl-9"
            placeholder="Search discussions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All tags" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All tags</SelectItem>
            {(data?.tags ?? []).map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 space-y-3">
        {data === undefined ? (
          <RowListSkeleton />
        ) : data.threads.length === 0 ? (
          <p className="animate-page-enter text-muted-foreground py-12 text-center">
            No discussions found. Start one!
          </p>
        ) : (
          data.threads.map((t, i) => (
            <Link
              key={t.id}
              to={`/discussions/${t.id}`}
              className="animate-item-enter block"
              style={{ "--stagger": i } as CSSProperties}
            >
              <Card className="ease-out-quart hover:ring-foreground/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
                <CardContent className="flex items-start gap-3">
                  <Avatar className="mt-0.5 size-9">
                    <AvatarImage
                      src={t.author.image_url ?? undefined}
                      alt={t.author.name}
                    />
                    <AvatarFallback>{t.author.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{t.title}</p>
                      {t.status === "pending" && (
                        <Badge variant="outline">Awaiting review</Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground mt-0.5 text-sm">
                      {t.author.name}
                      {t.author.org ? ` · ${t.author.org}` : ""} ·{" "}
                      {timeAgo(t.last_activity_at)}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {t.tags.map((tagName) => (
                        <Badge
                          key={tagName}
                          variant="secondary"
                          className="text-xs"
                        >
                          {tagName}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="text-muted-foreground flex items-center gap-1 text-sm">
                    <MessageSquare className="size-4" />
                    {t.reply_count}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
